import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import {
  BarChart3, TrendingUp, Users, Calendar, AlertTriangle,
  ChevronDown, ChevronUp, Shield
} from 'lucide-react';
import { getAbsenteeismAnalytics, getBradfordFactorReport } from '../../../api/Attendance/leaves';
import {
  AbsenteeismAnalyticsView,
  BradfordFactorReportView,
  DepartmentAbsenceRateView,
  AbsencePatternView,
  MonthlyAbsenceView,
  BradfordFactorView,
  DepartmentAbsenceSummaryView,
} from '../../../models/attendance';
import { PageShell } from '../../../components/leaves/PageShell';
import { getApiErrorMessage } from '../../../utils/apiErrors';

type TabType = 'absenteeism' | 'bradford';

interface Props {
  role: string;
}

const AbsenceAnalytics: React.FC<Props> = ({ role }) => {
  const { t } = useTranslation('leaves');
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [tab, setTab] = useState<TabType>('absenteeism');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [absenteeismData, setAbsenteeismData] = useState<AbsenteeismAnalyticsView | null>(null);
  const [bradfordData, setBradfordData] = useState<BradfordFactorReportView | null>(null);
  const [expandedDepts, setExpandedDepts] = useState<Set<number>>(new Set());

  const isHr = role === 'Hr' || role === 'Admin';

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (tab === 'absenteeism') {
        const result = await getAbsenteeismAnalytics(year);
        if (result.errors?.length) {
          setError(getApiErrorMessage(result, t, t('analytics.loadError')));
        } else {
          setAbsenteeismData(result.payload);
        }
      } else {
        const result = await getBradfordFactorReport(year);
        if (result.errors?.length) {
          setError(getApiErrorMessage(result, t, t('analytics.loadError')));
        } else {
          setBradfordData(result.payload);
        }
      }
    } catch (err) {
      setError(getApiErrorMessage(err, t, t('analytics.loadError')));
    } finally {
      setLoading(false);
    }
  }, [year, tab, t]);

  useEffect(() => {
    if (isHr) {
      loadData();
    }
  }, [isHr, loadData]);

  const toggleDept = (deptId: number) => {
    setExpandedDepts(prev => {
      const next = new Set(prev);
      if (next.has(deptId)) next.delete(deptId);
      else next.add(deptId);
      return next;
    });
  };

  if (!isHr) {
    return (
      <div className="p-6">
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-amber-800">
          <Shield className="inline w-5 h-5 ltr:mr-2 rtl:ml-2" />
          {t('analytics.hrOnly')}
        </div>
      </div>
    );
  }

  const riskBandColor = (band: string) => {
    switch (band) {
      case 'Low': return 'bg-green-100 text-green-800';
      case 'Medium': return 'bg-yellow-100 text-yellow-800';
      case 'High': return 'bg-orange-100 text-orange-800';
      case 'Critical': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const riskBandLabel = (band: string) => {
    switch (band) {
      case 'Low': return t('analytics.low');
      case 'Medium': return t('analytics.medium');
      case 'High': return t('analytics.high');
      case 'Critical': return t('analytics.critical');
      default: return band;
    }
  };

  const patternLabel = (type: string) => {
    switch (type) {
      case 'Monday': return t('analytics.monday');
      case 'Friday': return t('analytics.friday');
      case 'FrequentShort': return t('analytics.frequentShort');
      default: return type;
    }
  };

  const maxMonthlyDays = absenteeismData
    ? Math.max(...absenteeismData.monthlyTrend.map(m => m.totalDays), 1)
    : 1;

  const stats = useMemo(() => {
    const items: { label: string; value: string | number; color?: string }[] = [];
    if (tab === 'absenteeism' && absenteeismData) {
      items.push(
        { label: t('analytics.companyAbsenceRate'), value: `${absenteeismData.companyAbsenceRate.toFixed(1)}%` },
        // Scope is spelled out because the Bradford tab shows a DIFFERENT total under what used to be
        // the same words: this figure sums every approved request in the company, including requests
        // belonging to archived employees, while Bradford only scores employees still on the roster.
        { label: t('analytics.totalAbsenceDaysAllEmployees'), value: absenteeismData.totalAbsenceDays.toFixed(1), color: 'text-orange-600' },
        { label: t('analytics.totalEmployees'), value: absenteeismData.totalEmployees },
      );
    } else if (tab === 'bradford' && bradfordData) {
      items.push(
        // Absence days, not money: there is no per-employee daily rate available to this
        // report, so the old "Total Estimated Cost" tile was a hardcoded 500/day invention.
        // Active employees only: the server builds a Bradford row by joining each request back to the
        // non-archived Users set and drops the request when that lookup misses, so an archived
        // employee's absence days are absent from this sum. Different population, different label.
        { label: t('analytics.totalAbsenceDaysActiveEmployees'), value: bradfordData.employees.reduce((sum, e) => sum + e.totalDays, 0).toFixed(1), color: 'text-orange-600' },
        { label: t('analytics.totalEmployeesScored', 'Employees Scored'), value: bradfordData.employees.length },
      );
    }
    return items;
  }, [tab, absenteeismData, bradfordData, t]);

  return (
    <PageShell
      title={t('analytics.title')}
      description={t('analytics.description')}
      icon={BarChart3}
      accentColor="emerald"
      maxWidth="7xl"
      stats={stats}
      headerActions={
        <select
          value={year}
          onChange={e => setYear(Number(e.target.value))}
          className="rounded-lg border border-border px-3 py-2 text-sm bg-background text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
          data-testid="analytics-year-select"
        >
          {[currentYear, currentYear - 1, currentYear - 2].map(y => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
      }
    >
      <div className="space-y-6" data-testid="absence-analytics-page">
      {/* Tabs */}
      <div className="flex border-b border-border">
        <button
          onClick={() => setTab('absenteeism')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            tab === 'absenteeism'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
          data-testid="tab-absenteeism"
        >
          <BarChart3 className="inline w-4 h-4 ltr:mr-1.5 rtl:ml-1.5" />
          {t('analytics.absenteeism')}
        </button>
        <button
          onClick={() => setTab('bradford')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            tab === 'bradford'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
          data-testid="tab-bradford"
        >
          <TrendingUp className="inline w-4 h-4 ltr:mr-1.5 rtl:ml-1.5" />
          {t('analytics.bradfordFactor')}
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-12 text-muted-foreground">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 ltr:mr-3 rtl:ml-3" />
          {t('analytics.loading')}
        </div>
      )}

      {/* Absenteeism Tab */}
      {!loading && tab === 'absenteeism' && absenteeismData && (
        <div className="space-y-6">
          {/* Monthly Trend */}
          <div className="bg-card rounded-xl border border-border p-5 shadow-sm">
            <h3 className="text-sm font-semibold text-foreground mb-4">{t('analytics.monthlyTrend')}</h3>
            <div className="flex items-end gap-1.5 h-40" data-testid="monthly-trend-chart">
              {absenteeismData.monthlyTrend.map((m: MonthlyAbsenceView) => (
                <div key={m.month} className="flex-1 flex flex-col items-center gap-1">
                  <span className="text-xs text-muted-foreground">{m.totalDays.toFixed(0)}</span>
                  <div
                    className="w-full bg-blue-400 rounded-t transition-all hover:bg-blue-500"
                    style={{ height: `${(m.totalDays / maxMonthlyDays) * 100}%`, minHeight: m.totalDays > 0 ? '4px' : '0px' }}
                    title={`${m.monthName}: ${m.totalDays.toFixed(1)} ${t('analytics.days')}, ${m.requestCount} ${t('analytics.requests')}`}
                  />
                  <span className="text-xs text-muted-foreground/70">{m.monthName}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Department Rates */}
          <div className="bg-card rounded-xl border border-border p-5 shadow-sm">
            <h3 className="text-sm font-semibold text-foreground mb-4">{t('analytics.departmentRates')}</h3>
            {absenteeismData.departmentRates.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">{t('analytics.noDepartmentData')}</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="department-rates-table">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-start py-2 px-3 text-muted-foreground font-medium">{t('analytics.department')}</th>
                      <th className="text-end py-2 px-3 text-muted-foreground font-medium">{t('analytics.employees')}</th>
                      <th className="text-end py-2 px-3 text-muted-foreground font-medium">{t('analytics.absenceDays')}</th>
                      <th className="text-end py-2 px-3 text-muted-foreground font-medium">{t('analytics.absenceRate')}</th>
                      <th className="text-end py-2 px-3 text-muted-foreground font-medium">{t('analytics.avgAbsenceDays')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {absenteeismData.departmentRates.map((d: DepartmentAbsenceRateView) => (
                      <tr key={d.departmentId} className="border-b border-border/50 hover:bg-muted">
                        <td className="py-2 px-3 font-medium text-foreground">{d.departmentName}</td>
                        <td className="py-2 px-3 text-end text-muted-foreground">{d.employeeCount}</td>
                        <td className="py-2 px-3 text-end text-muted-foreground">{d.totalAbsenceDays.toFixed(1)}</td>
                        <td className="py-2 px-3 text-end">
                          <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                            d.absenceRate > 5 ? 'bg-red-100 text-red-700' :
                            d.absenceRate > 3 ? 'bg-yellow-100 text-yellow-700' :
                            'bg-green-100 text-green-700'
                          }`}>
                            {d.absenceRate.toFixed(1)}%
                          </span>
                        </td>
                        <td className="py-2 px-3 text-end text-muted-foreground">{d.averageAbsenceDays.toFixed(1)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Flagged Patterns */}
          <div className="bg-card rounded-xl border border-border p-5 shadow-sm">
            <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500" />
              {t('analytics.flaggedPatterns')}
            </h3>
            {absenteeismData.flaggedPatterns.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">{t('analytics.noPatternsDetected')}</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="patterns-table">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-start py-2 px-3 text-muted-foreground font-medium">{t('analytics.employee')}</th>
                      <th className="text-start py-2 px-3 text-muted-foreground font-medium">{t('analytics.department')}</th>
                      <th className="text-start py-2 px-3 text-muted-foreground font-medium">{t('analytics.patternType')}</th>
                      <th className="text-end py-2 px-3 text-muted-foreground font-medium">{t('analytics.occurrences')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {absenteeismData.flaggedPatterns.map((p: AbsencePatternView, idx: number) => (
                      <tr key={`${p.userId}-${p.patternType}-${idx}`} className="border-b border-border/50 hover:bg-muted">
                        <td className="py-2 px-3 font-medium text-foreground">{p.userName}</td>
                        <td className="py-2 px-3 text-muted-foreground">{p.departmentName || '—'}</td>
                        <td className="py-2 px-3">
                          <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700">
                            {patternLabel(p.patternType)}
                          </span>
                        </td>
                        <td className="py-2 px-3 text-end font-medium text-foreground">{p.occurrences}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Bradford Factor Tab */}
      {!loading && tab === 'bradford' && bradfordData && (
        <div className="space-y-6">
          {/* Bradford Factor Scores */}
          <div className="bg-card rounded-xl border border-border p-5 shadow-sm">
            <h3 className="text-sm font-semibold text-foreground mb-4">{t('analytics.bradfordFactor')}</h3>
            {bradfordData.employees.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">{t('analytics.noBradfordData')}</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="bradford-table">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-start py-2 px-3 text-muted-foreground font-medium">{t('analytics.employee')}</th>
                      <th className="text-start py-2 px-3 text-muted-foreground font-medium">{t('analytics.department')}</th>
                      <th className="text-end py-2 px-3 text-muted-foreground font-medium">{t('analytics.spells')}</th>
                      <th className="text-end py-2 px-3 text-muted-foreground font-medium">{t('analytics.totalDays')}</th>
                      <th className="text-end py-2 px-3 text-muted-foreground font-medium">{t('analytics.bradfordScore')}</th>
                      <th className="text-center py-2 px-3 text-muted-foreground font-medium">{t('analytics.riskBand')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bradfordData.employees.map((e: BradfordFactorView) => (
                      <tr key={e.userId} className="border-b border-border/50 hover:bg-muted">
                        <td className="py-2 px-3 font-medium text-foreground">{e.userName}</td>
                        <td className="py-2 px-3 text-muted-foreground">{e.departmentName || '—'}</td>
                        <td className="py-2 px-3 text-end text-muted-foreground">{e.spellCount}</td>
                        <td className="py-2 px-3 text-end text-muted-foreground">{e.totalDays.toFixed(1)}</td>
                        <td className="py-2 px-3 text-end font-bold text-foreground">{e.bradfordScore.toFixed(0)}</td>
                        <td className="py-2 px-3 text-center">
                          <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${riskBandColor(e.riskBand)}`}>
                            {riskBandLabel(e.riskBand)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Department Costs */}
          <div className="bg-card rounded-xl border border-border p-5 shadow-sm">
            <h3 className="text-sm font-semibold text-foreground mb-4">{t('analytics.departmentAbsenceDays')}</h3>
            {bradfordData.departmentBreakdown.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">{t('analytics.noDepartmentData')}</p>
            ) : (
              <div className="space-y-2" data-testid="department-breakdown">
                {bradfordData.departmentBreakdown.map((d: DepartmentAbsenceSummaryView) => (
                  <div key={d.departmentId} className="border border-border rounded-lg">
                    <button
                      onClick={() => toggleDept(d.departmentId)}
                      className="w-full flex items-center justify-between px-4 py-3 hover:bg-muted transition-colors"
                      data-testid={`dept-breakdown-${d.departmentId}`}
                    >
                      <div className="flex items-center gap-3">
                        <span className="font-medium text-foreground">{d.departmentName}</span>
                        <span className="text-xs text-muted-foreground/70">{d.employeeCount} {t('analytics.employees')}</span>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="text-sm font-semibold text-foreground">{d.totalAbsenceDays.toFixed(1)} {t('analytics.days')}</span>
                        {expandedDepts.has(d.departmentId) ? (
                          <ChevronUp className="w-4 h-4 text-muted-foreground" />
                        ) : (
                          <ChevronDown className="w-4 h-4 text-muted-foreground" />
                        )}
                      </div>
                    </button>
                    {expandedDepts.has(d.departmentId) && (
                      <div className="px-4 pb-3 border-t border-border pt-3 grid grid-cols-3 gap-4 text-sm">
                        <div>
                          <span className="text-muted-foreground">{t('analytics.absenceDays')}</span>
                          <p className="font-medium">{d.totalAbsenceDays.toFixed(1)}</p>
                        </div>
                        <div>
                          <span className="text-muted-foreground">{t('analytics.employees')}</span>
                          <p className="font-medium">{d.employeeCount}</p>
                        </div>
                        <div>
                          <span className="text-muted-foreground">{t('analytics.avgAbsenceDays')}</span>
                          <p className="font-medium">{d.employeeCount > 0 ? (d.totalAbsenceDays / d.employeeCount).toFixed(1) : '0.0'}</p>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
      </div>
    </PageShell>
  );
};

const mapStateToProps = (state: any) => ({
  role: state.user.role,
});

export default connect(mapStateToProps)(AbsenceAnalytics);
